import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ChartBarIcon,
    CheckIcon,
    ClipboardDocumentListIcon,
    DocumentTextIcon,
    ExclamationTriangleIcon,
    PencilIcon,
    XMarkIcon,
} from '@heroicons/react/24/outline';
import {
    Legend,
    PolarAngleAxis,
    PolarGrid,
    PolarRadiusAxis,
    Radar,
    RadarChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';
import Button from '@/components/Button/Button';
import { SubPageHeader } from '@/components/shared/SubPageHeader/SubPageHeader';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared';
import { useToast } from '@/hooks/useToast';
import {
    EvaluationCollaboratorResult,
    EvaluationNumericSummary,
    EvaluationResultGroup,
    EvaluationResultIndicator,
    getEvaluationCollaboratorResult,
} from '../services/evaluationResults';
import {
    EvaluationQualitativeReportContent,
    generateEvaluationQualitativeReport,
    getCachedEvaluationQualitativeReport,
    saveEvaluationQualitativeReportOverride,
} from '../services/evaluationQualitativeReport';
import { EvaluationInviteType, saveEvaluationResponse } from '../services/evaluationsService';

const evaluatorTypeLabels: Record<EvaluationInviteType, string> = {
    SELF: 'Autoavaliação',
    LEADER: 'Líder',
    CLIENT: 'Cliente',
};

const evaluatorTypes: EvaluationInviteType[] = ['SELF', 'LEADER', 'CLIENT'];

const evaluatorTypeClasses: Record<EvaluationInviteType, string> = {
    SELF: 'bg-sky-50 text-sky-700 border-sky-200',
    LEADER: 'bg-violet-50 text-violet-700 border-violet-200',
    CLIENT: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const scoreTone = (percentage: number | null) => {
    if (percentage === null) return 'bg-gray-50 text-gray-500 border-gray-200';
    if (percentage >= 0.85) return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (percentage >= 0.7) return 'bg-sky-50 text-sky-700 border-sky-200';
    if (percentage >= 0.5) return 'bg-amber-50 text-amber-700 border-amber-200';
    return 'bg-red-50 text-red-700 border-red-200';
};

const formatScore = (value?: number | null) => (
    typeof value === 'number' && Number.isFinite(value)
        ? value.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
        : '-'
);

const formatPercent = (value?: number | null) => (
    typeof value === 'number' && Number.isFinite(value)
        ? value.toLocaleString('pt-BR', { style: 'percent', maximumFractionDigits: 0 })
        : '-'
);

const formatDate = (value?: Date | null) => (
    value ? value.toLocaleDateString('pt-BR') : '-'
);

const formatGap = (value?: number | null) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return '-';
    }

    return value > 0 ? `+${formatScore(value)}` : formatScore(value);
};

interface EvaluationEditDraftForType {
    answers: Record<string, string>;
    comments: Record<string, string>;
}

type EvaluationEditDraft = Partial<Record<EvaluationInviteType, EvaluationEditDraftForType>>;

interface EditableIndicatorItem {
    id: string;
    description: string;
    question: string;
    maxScore: number;
    groupDescription: string;
    behaviorDescription: string;
}

type QualitativeStringListField = 'overview' | 'finalConsiderations';
type QualitativeNarrativeField = 'excellence' | 'improvements' | 'pdi';

function isScoreGroup(group: EvaluationResultGroup): boolean {
    return !group.type || group.type === 'STANDARD';
}

function getEditableIndicators(result: EvaluationCollaboratorResult): EditableIndicatorItem[] {
    return result.groups.flatMap((group) => (
        group.behaviors.flatMap((behavior) => (
            behavior.indicators.map((indicator) => ({
                id: indicator.id,
                description: indicator.description,
                question: indicator.question,
                maxScore: indicator.maxScore,
                groupDescription: group.description,
                behaviorDescription: behavior.description,
            }))
        ))
    ));
}

function buildEditDraft(result: EvaluationCollaboratorResult): EvaluationEditDraft {
    return evaluatorTypes.reduce<EvaluationEditDraft>((draft, type) => {
        const response = result.completedResponses[type];
        if (!response) {
            return draft;
        }

        draft[type] = {
            answers: Object.fromEntries(
                Object.entries(response.answers ?? {}).map(([indicatorId, value]) => [
                    indicatorId,
                    typeof value === 'number' && Number.isFinite(value) ? String(value) : '',
                ])
            ),
            comments: { ...(response.comments ?? {}) },
        };

        return draft;
    }, {});
}

function cloneQualitativeReport(content: EvaluationQualitativeReportContent): EvaluationQualitativeReportContent {
    return {
        overview: [...content.overview],
        excellence: content.excellence.map((item) => ({ ...item })),
        improvements: content.improvements.map((item) => ({ ...item })),
        pdi: content.pdi.map((item) => ({ ...item })),
        finalConsiderations: [...content.finalConsiderations],
    };
}

function validateQualitativeReport(content: EvaluationQualitativeReportContent): string | null {
    if (content.overview.length === 0 || content.overview.some((paragraph) => !paragraph.trim())) {
        return 'Preencha todos os parágrafos de visão geral.';
    }

    if (content.finalConsiderations.length === 0 || content.finalConsiderations.some((paragraph) => !paragraph.trim())) {
        return 'Preencha todos os parágrafos de considerações finais.';
    }

    const narrativeSections: Array<{
        label: string;
        items: Array<{ title: string; body: string }>;
    }> = [
        { label: 'pontos de excelência', items: content.excellence },
        { label: 'pontos de melhoria', items: content.improvements },
        { label: 'PDI', items: content.pdi },
    ];

    const invalidSection = narrativeSections.find((section) => (
        section.items.length === 0 ||
        section.items.some((item) => !item.title.trim() || !item.body.trim())
    ));

    return invalidSection ? `Preencha títulos e textos em ${invalidSection.label}.` : null;
}

function parseScoreInput(value: string): number | null {
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }

    const parsed = Number(trimmed.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function SummaryMetric({
    label,
    summary,
    helper,
}: {
    label: string;
    summary: EvaluationNumericSummary;
    helper: string;
}) {
    return (
        <div className={`rounded-2xl border p-5 shadow-sm ${scoreTone(summary.percentage)}`}>
            <p className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</p>
            <p className="mt-2 text-3xl font-semibold">{formatPercent(summary.percentage)}</p>
            <p className="mt-1 text-sm opacity-80">
                {summary.count > 0 ? `${formatScore(summary.score)} de ${formatScore(summary.maxScore)} ponto(s)` : 'Sem resposta concluída'}
            </p>
            <p className="mt-4 text-xs opacity-80">{helper}</p>
        </div>
    );
}

function InfoItem({ label, value }: { label: string; value?: string | null }) {
    return (
        <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
            <p className="mt-1 text-sm font-medium text-gray-900">{value || '-'}</p>
        </div>
    );
}

function ScoreCell({ value, maxScore }: { value?: number | null; maxScore?: number }) {
    const percentage = typeof value === 'number' && maxScore ? value / maxScore : null;

    return (
        <span className={`inline-flex min-w-[3rem] justify-center rounded-full border px-2 py-1 text-xs font-semibold ${scoreTone(percentage)}`}>
            {formatScore(value)}
        </span>
    );
}

function EditableScoreInput({
    value,
    maxScore,
    label,
    disabled,
    onChange,
}: {
    value: string;
    maxScore: number;
    label: string;
    disabled?: boolean;
    onChange: (value: string) => void;
}) {
    return (
        <input
            aria-label={label}
            className="h-9 w-20 rounded-lg border border-gray-300 bg-white px-2 text-center text-sm font-semibold text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:bg-gray-100 disabled:text-gray-400"
            disabled={disabled}
            inputMode="decimal"
            max={maxScore}
            min={0}
            onChange={(event) => onChange(event.target.value)}
            step={1}
            type="number"
            value={value}
        />
    );
}

function PercentCell({ value }: { value?: number | null }) {
    return (
        <span className={`inline-flex min-w-[4rem] justify-center rounded-full border px-2 py-1 text-xs font-semibold ${scoreTone(value ?? null)}`}>
            {formatPercent(value)}
        </span>
    );
}

function ResultRadar({ result }: { result: EvaluationCollaboratorResult }) {
    const scoreGroups = result.groups.filter(isScoreGroup);
    const chartData = scoreGroups.map((group) => ({
        group: group.description,
        media: group.aggregate.mean.percentage !== null ? Math.round(group.aggregate.mean.percentage * 100) : null,
        auto: group.aggregate.byType.SELF.percentage !== null ? Math.round(group.aggregate.byType.SELF.percentage * 100) : null,
        lider: group.aggregate.byType.LEADER.percentage !== null ? Math.round(group.aggregate.byType.LEADER.percentage * 100) : null,
        cliente: group.aggregate.byType.CLIENT.percentage !== null ? Math.round(group.aggregate.byType.CLIENT.percentage * 100) : null,
        esperado: group.expectedPercentage !== null ? Math.round(group.expectedPercentage * 100) : null,
    }));
    const hasExpected = chartData.some((item) => item.esperado !== null);
    const hasClient = chartData.some((item) => item.cliente !== null);

    if (chartData.length === 0) {
        return null;
    }

    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Gráfico Raro Skills</h2>
                    <p className="mt-1 text-sm text-gray-500">Percentual por eixo, sem considerar respostas 0 nem prólogo.</p>
                </div>
            </div>
            <div className="mt-4 h-[340px]">
                <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={chartData} outerRadius="72%">
                        <PolarGrid />
                        <PolarAngleAxis dataKey="group" tick={{ fontSize: 12, fill: '#4b5563' }} />
                        <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fontSize: 11, fill: '#6b7280' }} tickFormatter={(value) => `${value}%`} />
                        <Radar name="Média" dataKey="media" stroke="#0f766e" fill="#0f766e" fillOpacity={0.16} />
                        <Radar name="Autoavaliação" dataKey="auto" stroke="#0284c7" fill="#0284c7" fillOpacity={0.08} />
                        <Radar name="Líder" dataKey="lider" stroke="#7c3aed" fill="#7c3aed" fillOpacity={0.08} />
                        {hasClient ? (
                            <Radar name="Cliente" dataKey="cliente" stroke="#16a34a" fill="#16a34a" fillOpacity={0.08} />
                        ) : null}
                        {hasExpected ? (
                            <Radar name="Esperado pela Raro" dataKey="esperado" stroke="#ea580c" fill="#ea580c" fillOpacity={0.04} />
                        ) : null}
                        <Legend />
                        <Tooltip formatter={(value) => [`${value}%`, '']} />
                    </RadarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}

function AttentionList({
    title,
    items,
    mode,
}: {
    title: string;
    items: EvaluationResultIndicator[];
    mode: 'score' | 'gap';
}) {
    return (
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            {items.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500">Sem dados suficientes para este recorte.</p>
            ) : (
                <div className="mt-4 space-y-3">
                    {items.map((item) => (
                        <div key={`${mode}-${item.id}`} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                                <p className="text-sm font-medium text-gray-900">{item.description}</p>
                                {mode === 'score' ? (
                                    <PercentCell value={item.percentage} />
                                ) : (
                                    <span className={`rounded-full border px-2 py-1 text-xs font-semibold ${Math.abs(item.gapSelfEvaluator ?? 0) >= 2 ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
                                        {formatGap(item.gapSelfEvaluator)}
                                    </span>
                                )}
                            </div>
                            <p className="mt-1 text-xs text-gray-500">{item.question}</p>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function ExpectedTable({
    groups,
    expectedContextLabel,
}: {
    groups: EvaluationResultGroup[];
    expectedContextLabel: string;
}) {
    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Esperado pela Raro</h2>
                    <p className="mt-1 text-sm text-gray-500">Comparação com o parâmetro configurado por cargo e senioridade.</p>
                </div>
                {expectedContextLabel ? (
                    <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-600">
                        {expectedContextLabel}
                    </span>
                ) : null}
            </div>
            <div className="mt-4 w-full overflow-x-auto">
                <table className="w-full min-w-[720px] divide-y divide-gray-200 text-sm">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-4 py-3 text-left font-semibold text-gray-700">Eixo</th>
                            <th className="px-4 py-3 text-center font-semibold text-gray-700">Média</th>
                            <th className="px-4 py-3 text-center font-semibold text-gray-700">Esperado pela Raro</th>
                            <th className="px-4 py-3 text-center font-semibold text-gray-700">Status</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {groups.map((group) => (
                            <tr key={group.id}>
                                <td className="px-4 py-3 font-medium text-gray-900">{group.description}</td>
                                <td className="px-4 py-3 text-center"><PercentCell value={group.aggregate.mean.percentage} /></td>
                                <td className="px-4 py-3 text-center">{formatPercent(group.expectedPercentage)}</td>
                                <td className="px-4 py-3 text-center">
                                    {group.expectedPercentage === null ? (
                                        <span className="rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-xs font-semibold text-gray-500">Sem referência</span>
                                    ) : group.belowExpected ? (
                                        <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-semibold text-amber-700">Abaixo</span>
                                    ) : (
                                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">Dentro</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function GroupSection({
    group,
    visibleTypes,
    gapEvaluatorLabel,
    editDraft,
    isEditing,
    onAnswerChange,
}: {
    group: EvaluationResultGroup;
    visibleTypes: EvaluationInviteType[];
    gapEvaluatorLabel: string;
    editDraft: EvaluationEditDraft;
    isEditing: boolean;
    onAnswerChange: (type: EvaluationInviteType, indicatorId: string, value: string) => void;
}) {
    const scoreGroup = isScoreGroup(group);

    return (
        <section className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-col gap-3 border-b border-gray-200 bg-gray-50 px-5 py-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">{group.description}</h2>
                    <p className="mt-1 text-sm text-gray-500">
                        {scoreGroup ? (
                            <>
                                Média {formatPercent(group.aggregate.mean.percentage)}
                                {group.expectedPercentage !== null ? ` - Esperado pela Raro ${formatPercent(group.expectedPercentage)}` : ''}
                            </>
                        ) : 'Bloco informativo da avaliação.'}
                    </p>
                </div>
                {scoreGroup ? (
                    <div className="flex flex-wrap gap-2">
                        <PercentCell value={group.aggregate.byType.SELF.percentage} />
                        <PercentCell value={group.aggregate.byType.LEADER.percentage} />
                        <PercentCell value={group.aggregate.mean.percentage} />
                    </div>
                ) : null}
            </div>

            <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[960px] divide-y divide-gray-200 text-sm">
                    <thead className="bg-white">
                        <tr>
                            <th className="px-5 py-3 text-left font-semibold text-gray-700">Comportamento</th>
                            <th className="px-5 py-3 text-left font-semibold text-gray-700">Indicador</th>
                            {visibleTypes.map((type) => (
                                <th key={type} className="px-3 py-3 text-center font-semibold text-gray-700">
                                    {evaluatorTypeLabels[type]}
                                </th>
                            ))}
                            <th className="px-3 py-3 text-center font-semibold text-gray-700">Média</th>
                            <th className="px-3 py-3 text-center font-semibold text-gray-700">% Média</th>
                            <th className="px-3 py-3 text-center font-semibold text-gray-700">Gap {gapEvaluatorLabel}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                        {group.behaviors.map((behavior) => (
                            <React.Fragment key={behavior.id}>
                                {behavior.indicators.map((indicator, index) => (
                                    <tr key={indicator.id}>
                                        <td className="w-56 px-5 py-3 align-top font-medium text-gray-900">
                                            {index === 0 ? behavior.description : ''}
                                        </td>
                                        <td className="min-w-[22rem] px-5 py-3 align-top text-gray-700">
                                            {indicator.description}
                                        </td>
                                        {visibleTypes.map((type) => (
                                            <td key={type} className="px-3 py-3 text-center align-top">
                                                {isEditing && editDraft[type] ? (
                                                    <EditableScoreInput
                                                        label={`${evaluatorTypeLabels[type]} - ${indicator.description}`}
                                                        maxScore={indicator.maxScore}
                                                        onChange={(value) => onAnswerChange(type, indicator.id, value)}
                                                        value={editDraft[type]?.answers[indicator.id] ?? ''}
                                                    />
                                                ) : (
                                                    <ScoreCell value={indicator.answers[type]} maxScore={indicator.maxScore} />
                                                )}
                                            </td>
                                        ))}
                                        <td className="px-3 py-3 text-center align-top">
                                            <ScoreCell value={indicator.average} maxScore={indicator.maxScore} />
                                        </td>
                                        <td className="px-3 py-3 text-center align-top">
                                            <PercentCell value={indicator.percentage} />
                                        </td>
                                        <td className="px-3 py-3 text-center align-top text-gray-600">
                                            {formatGap(indicator.gapSelfEvaluator)}
                                        </td>
                                    </tr>
                                ))}
                                {scoreGroup ? (
                                    <tr className="bg-gray-50">
                                        <td className="px-5 py-3 font-semibold text-gray-900" colSpan={2}>
                                            Total {behavior.description}
                                        </td>
                                        {visibleTypes.map((type) => (
                                            <td key={type} className="px-3 py-3 text-center">
                                                <ScoreCell value={behavior.aggregate.byType[type].score} maxScore={behavior.aggregate.byType[type].maxScore} />
                                            </td>
                                        ))}
                                        <td className="px-3 py-3 text-center">
                                            <ScoreCell value={behavior.aggregate.mean.score} maxScore={behavior.aggregate.mean.maxScore} />
                                        </td>
                                        <td className="px-3 py-3 text-center">
                                            <PercentCell value={behavior.aggregate.mean.percentage} />
                                        </td>
                                        <td className="px-3 py-3" />
                                    </tr>
                                ) : null}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function ResultCommentsSection({
    result,
    visibleTypes,
    editDraft,
    isEditing,
    onCommentChange,
}: {
    result: EvaluationCollaboratorResult;
    visibleTypes: EvaluationInviteType[];
    editDraft: EvaluationEditDraft;
    isEditing: boolean;
    onCommentChange: (type: EvaluationInviteType, indicatorId: string, value: string) => void;
}) {
    if (isEditing) {
        const editableIndicators = getEditableIndicators(result);

        return (
            <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Textos e observações</h2>
                        <p className="mt-1 text-sm text-gray-500">As alterações são salvas nas respostas concluídas de cada avaliador.</p>
                    </div>
                </div>

                <div className="mt-5 space-y-4">
                    {editableIndicators.map((indicator) => (
                        <div key={indicator.id} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                            <div className="flex flex-col gap-1">
                                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                                    {indicator.groupDescription} / {indicator.behaviorDescription}
                                </p>
                                <h3 className="text-sm font-semibold text-gray-900">{indicator.description}</h3>
                                <p className="text-xs text-gray-500">{indicator.question}</p>
                            </div>

                            <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
                                {visibleTypes.map((type) => {
                                    const draftForType = editDraft[type];

                                    return (
                                        <label key={`${indicator.id}-${type}`} className="block">
                                            <span className="text-xs font-semibold text-gray-600">{evaluatorTypeLabels[type]}</span>
                                            <textarea
                                                className="mt-1 min-h-[92px] w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 disabled:bg-gray-100 disabled:text-gray-400"
                                                disabled={!draftForType}
                                                onChange={(event) => onCommentChange(type, indicator.id, event.target.value)}
                                                value={draftForType?.comments[indicator.id] ?? ''}
                                            />
                                        </label>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <h2 className="text-lg font-semibold text-gray-900">Observações</h2>
            {result.comments.length === 0 ? (
                <p className="mt-4 text-sm text-gray-500">Nenhuma observação registrada nas respostas desta avaliação.</p>
            ) : (
                <div className="mt-4 space-y-3">
                    {result.comments.map((comment, index) => (
                        <div key={`${comment.type}-${comment.indicatorId}-${index}`} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                            <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-semibold ${evaluatorTypeClasses[comment.type]}`}>
                                {evaluatorTypeLabels[comment.type]}
                            </span>
                            <p className="mt-2 text-sm text-gray-700">{comment.text}</p>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

function QualitativeReportEditor({
    draft,
    error,
    loading,
    onNarrativeChange,
    onStringListChange,
}: {
    draft: EvaluationQualitativeReportContent | null;
    error: string | null;
    loading: boolean;
    onNarrativeChange: (field: QualitativeNarrativeField, index: number, key: 'title' | 'body', value: string) => void;
    onStringListChange: (field: QualitativeStringListField, index: number, value: string) => void;
}) {
    const narrativeSections: Array<{
        field: QualitativeNarrativeField;
        title: string;
    }> = [
        { field: 'excellence', title: 'Pontos de excelência' },
        { field: 'improvements', title: 'Pontos de melhoria' },
        { field: 'pdi', title: 'Plano de desenvolvimento individual' },
    ];

    return (
        <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h2 className="text-lg font-semibold text-gray-900">Textos do relatório</h2>
                    <p className="mt-1 text-sm text-gray-500">Conteúdo qualitativo usado no PDF de impressão.</p>
                </div>
                {loading ? (
                    <span className="rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700">
                        Carregando textos
                    </span>
                ) : null}
            </div>

            {error ? (
                <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                    {error}
                </div>
            ) : null}

            {!draft && !loading ? (
                <p className="mt-4 text-sm text-gray-500">Nenhum texto qualitativo disponível para edição.</p>
            ) : null}

            {draft ? (
                <div className="mt-5 space-y-6">
                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">Visão geral</h3>
                        <div className="mt-3 space-y-3">
                            {draft.overview.map((paragraph, index) => (
                                <textarea
                                    key={`overview-${index}`}
                                    className="min-h-[96px] w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                                    onChange={(event) => onStringListChange('overview', index, event.target.value)}
                                    value={paragraph}
                                />
                            ))}
                        </div>
                    </div>

                    {narrativeSections.map((section) => (
                        <div key={section.field}>
                            <h3 className="text-sm font-semibold text-gray-900">{section.title}</h3>
                            <div className="mt-3 space-y-4">
                                {draft[section.field].map((item, index) => (
                                    <div key={`${section.field}-${index}`} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                                        <input
                                            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                                            onChange={(event) => onNarrativeChange(section.field, index, 'title', event.target.value)}
                                            value={item.title}
                                        />
                                        <textarea
                                            className="mt-3 min-h-[104px] w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                                            onChange={(event) => onNarrativeChange(section.field, index, 'body', event.target.value)}
                                            value={item.body}
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    <div>
                        <h3 className="text-sm font-semibold text-gray-900">Considerações finais</h3>
                        <div className="mt-3 space-y-3">
                            {draft.finalConsiderations.map((paragraph, index) => (
                                <textarea
                                    key={`final-${index}`}
                                    className="min-h-[96px] w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20"
                                    onChange={(event) => onStringListChange('finalConsiderations', index, event.target.value)}
                                    value={paragraph}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            ) : null}
        </section>
    );
}

export const EvaluationCollaboratorResultPage: React.FC = () => {
    const { id, collaboratorId } = useParams<{ id: string; collaboratorId: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const [result, setResult] = useState<EvaluationCollaboratorResult | null>(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editDraft, setEditDraft] = useState<EvaluationEditDraft>({});
    const [saving, setSaving] = useState(false);
    const [qualitativeDraft, setQualitativeDraft] = useState<EvaluationQualitativeReportContent | null>(null);
    const [qualitativeLoading, setQualitativeLoading] = useState(false);
    const [qualitativeError, setQualitativeError] = useState<string | null>(null);

    const loadResult = useCallback(async () => {
        if (!id || !collaboratorId) return;

        setLoading(true);
        try {
            const data = await getEvaluationCollaboratorResult(id, collaboratorId);
            setResult(data);
            setLoadError(null);
            if (!data) {
                setLoadError('Não encontramos este colaborador dentro da campanha.');
            }
        } catch (error) {
            console.error(error);
            setResult(null);
            setLoadError('Não foi possível carregar o resultado individual.');
            toast.error('Erro', 'Não foi possível carregar o resultado individual.');
        } finally {
            setLoading(false);
        }
    }, [collaboratorId, id, toast]);

    useEffect(() => {
        void loadResult();
    }, [loadResult]);

    const loadQualitativeReport = useCallback(async () => {
        if (!id || !collaboratorId) return;

        setQualitativeLoading(true);
        setQualitativeError(null);
        try {
            const cached = await getCachedEvaluationQualitativeReport({ campaignId: id, collaboratorId });
            const content = cached ?? (await generateEvaluationQualitativeReport({ campaignId: id, collaboratorId })).content;
            setQualitativeDraft(cloneQualitativeReport(content));
        } catch (error) {
            console.error(error);
            setQualitativeDraft(null);
            setQualitativeError('Não foi possível carregar os textos qualitativos para edição.');
        } finally {
            setQualitativeLoading(false);
        }
    }, [collaboratorId, id]);

    const visibleTypes = useMemo<EvaluationInviteType[]>(() => {
        if (!result) return ['SELF', 'LEADER'];
        return result.overall.byType.CLIENT.count > 0
            ? ['SELF', 'LEADER', 'CLIENT']
            : ['SELF', 'LEADER'];
    }, [result]);

    const gapEvaluatorLabel = useMemo(() => (
        result && result.overall.byType.LEADER.count === 0 && result.overall.byType.CLIENT.count > 0
            ? 'cliente'
            : 'líder'
    ), [result]);

    const editableIndicators = useMemo(
        () => (result ? getEditableIndicators(result) : []),
        [result]
    );

    const indicatorMaxScoreById = useMemo(() => (
        editableIndicators.reduce<Record<string, number>>((acc, indicator) => {
            acc[indicator.id] = indicator.maxScore;
            return acc;
        }, {})
    ), [editableIndicators]);

    const handleStartEditing = useCallback(() => {
        if (!result) return;
        setEditDraft(buildEditDraft(result));
        setIsEditing(true);
        void loadQualitativeReport();
    }, [loadQualitativeReport, result]);

    const handleCancelEditing = useCallback(() => {
        if (!saving) {
            setEditDraft({});
            setQualitativeDraft(null);
            setQualitativeError(null);
            setIsEditing(false);
        }
    }, [saving]);

    const handleAnswerChange = useCallback((type: EvaluationInviteType, indicatorId: string, value: string) => {
        setEditDraft((current) => {
            const draftForType = current[type] ?? { answers: {}, comments: {} };

            return {
                ...current,
                [type]: {
                    answers: {
                        ...draftForType.answers,
                        [indicatorId]: value,
                    },
                    comments: draftForType.comments,
                },
            };
        });
    }, []);

    const handleCommentChange = useCallback((type: EvaluationInviteType, indicatorId: string, value: string) => {
        setEditDraft((current) => {
            const draftForType = current[type] ?? { answers: {}, comments: {} };

            return {
                ...current,
                [type]: {
                    answers: draftForType.answers,
                    comments: {
                        ...draftForType.comments,
                        [indicatorId]: value,
                    },
                },
            };
        });
    }, []);

    const handleQualitativeStringListChange = useCallback((field: QualitativeStringListField, index: number, value: string) => {
        setQualitativeDraft((current) => {
            if (!current) return current;
            const nextValues = [...current[field]];
            nextValues[index] = value;

            return {
                ...current,
                [field]: nextValues,
            };
        });
    }, []);

    const handleQualitativeNarrativeChange = useCallback((
        field: QualitativeNarrativeField,
        index: number,
        key: 'title' | 'body',
        value: string
    ) => {
        setQualitativeDraft((current) => {
            if (!current) return current;
            const nextItems = current[field].map((item, itemIndex) => (
                itemIndex === index ? { ...item, [key]: value } : item
            ));

            return {
                ...current,
                [field]: nextItems,
            };
        });
    }, []);

    const handleSaveEditing = useCallback(async () => {
        if (!result) return;

        const editableIndicatorIds = new Set(editableIndicators.map((indicator) => indicator.id));
        const nextResponses = evaluatorTypes.reduce<Array<{
            type: EvaluationInviteType;
            draft: EvaluationEditDraftForType;
            response: NonNullable<EvaluationCollaboratorResult['completedResponses'][EvaluationInviteType]>;
        }>>((acc, type) => {
            const draft = editDraft[type];
            const response = result.completedResponses[type];
            if (draft && response) {
                acc.push({ type, draft, response });
            }
            return acc;
        }, []);

        if (nextResponses.length === 0) {
            toast.error('Nada para salvar', 'Não há respostas concluídas para sobrescrever.');
            return;
        }

        const validationErrors: string[] = [];
        const payloads = nextResponses.map(({ type, draft, response }) => {
            const nextAnswers = { ...(response?.answers ?? {}) };
            const nextComments = { ...(response?.comments ?? {}) };

            editableIndicatorIds.forEach((indicatorId) => {
                const rawScore = draft?.answers[indicatorId] ?? '';
                const parsedScore = parseScoreInput(rawScore);
                const maxScore = indicatorMaxScoreById[indicatorId] ?? 5;

                if (Number.isNaN(parsedScore)) {
                    validationErrors.push(`${evaluatorTypeLabels[type]}: nota inválida em "${indicatorId}".`);
                    return;
                }

                if (parsedScore === null || parsedScore <= 0) {
                    delete nextAnswers[indicatorId];
                } else if (parsedScore > maxScore) {
                    validationErrors.push(`${evaluatorTypeLabels[type]}: nota maior que ${formatScore(maxScore)} em "${indicatorId}".`);
                } else {
                    nextAnswers[indicatorId] = parsedScore;
                }

                const comment = draft?.comments[indicatorId]?.trim() ?? '';
                if (comment) {
                    nextComments[indicatorId] = comment;
                } else {
                    delete nextComments[indicatorId];
                }
            });

            return {
                id: response?.id,
                campaignId: response.campaignId,
                collaboratorId: response.collaboratorId,
                evaluatorId: response.evaluatorId,
                evaluatorEmail: response.evaluatorEmail,
                type,
                status: 'completed' as const,
                answers: nextAnswers,
                comments: nextComments,
            };
        });

        if (validationErrors.length > 0) {
            toast.error('Revise as notas', validationErrors[0]);
            return;
        }

        const qualitativeValidationError = qualitativeDraft ? validateQualitativeReport(qualitativeDraft) : null;
        if (qualitativeValidationError) {
            toast.error('Revise os textos do relatório', qualitativeValidationError);
            return;
        }

        setSaving(true);
        try {
            await Promise.all(payloads.map((payload) => saveEvaluationResponse(payload)));
            if (qualitativeDraft && id && collaboratorId) {
                await saveEvaluationQualitativeReportOverride({
                    campaignId: id,
                    collaboratorId,
                    content: qualitativeDraft,
                });
            }
            toast.success('Resultado salvo', 'Notas e textos foram atualizados.');
            setIsEditing(false);
            setEditDraft({});
            setQualitativeDraft(null);
            await loadResult();
        } catch (error) {
            console.error(error);
            toast.error('Erro', 'Não foi possível salvar as alterações do resultado.');
        } finally {
            setSaving(false);
        }
    }, [collaboratorId, editDraft, editableIndicators, id, indicatorMaxScoreById, loadResult, qualitativeDraft, result, toast]);

    if (loading) {
        return (
            <div className="rounded-2xl border border-gray-200 bg-white p-8 shadow-sm">
                <p className="text-sm font-medium text-gray-900">Carregando resultado individual...</p>
                <p className="mt-2 text-sm text-gray-500">Consolidando respostas, médias e percentuais da campanha.</p>
            </div>
        );
    }

    if (loadError || !result) {
        return (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-8 shadow-sm">
                <div className="flex items-start gap-3">
                    <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 text-red-600" aria-hidden />
                    <div className="space-y-4">
                        <div>
                            <h1 className="text-lg font-semibold text-red-900">Resultado não disponível</h1>
                            <p className="mt-1 text-sm text-red-800">{loadError}</p>
                        </div>
                        <div className="flex flex-wrap gap-3">
                            <Button variant="primary" onClick={() => void loadResult()}>
                                Tentar novamente
                            </Button>
                            <Button variant="outline" onClick={() => navigate(`/people/evaluations/${id}`)}>
                                Voltar para campanha
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const period = `${result.campaign.startDate.toLocaleDateString('pt-BR')} até ${result.campaign.endDate.toLocaleDateString('pt-BR')}`;
    const projectLabel = [
        result.collaborator.clientName,
        result.collaborator.projectName,
    ].filter(Boolean).join(' - ')
        || result.collaborator.clientResponsibleName;
    const expectedContextLabel = [
        result.collaborator.positionName,
        result.collaborator.seniorityName,
    ].filter(Boolean).join(' - ');
    const scoreGroups = result.groups.filter(isScoreGroup);

    return (
        <div className="space-y-6">
            <SubPageHeader
                backTo={`/people/evaluations/${id}`}
                icon={<ClipboardDocumentListIcon className="h-6 w-6" aria-hidden />}
                badge="Resultado individual"
                title={result.collaborator.collaboratorName}
                description={`${result.campaign.questionnaireTitle || 'Avaliação de desempenho'} - ${period}`}
                actions={
                    <div className="flex flex-wrap gap-2">
                        {isEditing ? (
                            <>
                                <Button
                                    variant="outline"
                                    disabled={saving}
                                    onClick={handleCancelEditing}
                                    leftIcon={<XMarkIcon className="h-4 w-4" aria-hidden />}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    disabled={qualitativeLoading}
                                    loading={saving}
                                    onClick={() => void handleSaveEditing()}
                                    leftIcon={<CheckIcon className="h-4 w-4" aria-hidden />}
                                >
                                    Salvar alterações
                                </Button>
                            </>
                        ) : (
                            <>
                                <Button
                                    variant="outline"
                                    onClick={handleStartEditing}
                                    leftIcon={<PencilIcon className="h-4 w-4" aria-hidden />}
                                >
                                    Editar resultado
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => navigate(`/people/evaluations/${id}/results/${collaboratorId}/report`)}
                                    leftIcon={<DocumentTextIcon className="h-4 w-4" aria-hidden />}
                                >
                                    Relatório para impressão
                                </Button>
                            </>
                        )}
                    </div>
                }
            />

            {isEditing ? (
                <section className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900 shadow-sm">
                    <p className="font-semibold">Modo de edição ativo</p>
                    <p className="mt-1">Notas e observações serão salvas por cima das respostas concluídas. Médias, gaps e percentuais serão recalculados após salvar.</p>
                </section>
            ) : null}

            <section className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex min-w-0 items-center gap-4">
                        <CollaboratorAvatar
                            photoUrl={result.collaborator.collaboratorPhotoUrl}
                            name={result.collaborator.collaboratorName}
                            size="large"
                        />
                        <div className="min-w-0">
                            <h2 className="truncate text-xl font-semibold text-gray-900">{result.collaborator.collaboratorName}</h2>
                            <p className="mt-1 text-sm text-gray-500">{result.collaborator.collaboratorEmail || '-'}</p>
                            <div className="mt-3 flex flex-wrap gap-2">
                                {result.evaluatorStatuses.map((status) => (
                                    <span
                                        key={status.type}
                                        className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${status.completed ? evaluatorTypeClasses[status.type] : 'border-gray-200 bg-gray-50 text-gray-500'}`}
                                    >
                                        {status.label}: {status.completed ? `concluída em ${formatDate(status.completedAt)}` : 'pendente'}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2 lg:w-[34rem]">
                        <InfoItem label="Cargo" value={result.collaborator.positionName} />
                        <InfoItem label="Senioridade" value={result.collaborator.seniorityName} />
                        <InfoItem label="Líder" value={result.collaborator.responsibleName} />
                        <InfoItem label="Cliente/Projeto" value={projectLabel} />
                    </div>
                </div>
            </section>

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
                <SummaryMetric
                    label="Média geral"
                    summary={result.overall.mean}
                    helper="Média dos eixos avaliáveis, sem respostas 0 e sem prólogo."
                />
                <SummaryMetric
                    label="Autoavaliação"
                    summary={result.overall.byType.SELF}
                    helper="Percentual respondido pelo colaborador."
                />
                <SummaryMetric
                    label="Avaliação líder"
                    summary={result.overall.byType.LEADER}
                    helper="Percentual respondido pelo líder."
                />
                <SummaryMetric
                    label="Avaliação cliente"
                    summary={result.overall.byType.CLIENT}
                    helper="Percentual respondido por avaliador externo."
                />
            </div>

            <ResultRadar result={result} />

            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
                <AttentionList
                    title="Menores médias"
                    items={result.lowScoreIndicators}
                    mode="score"
                />
                <AttentionList
                    title={`Maiores diferenças ${gapEvaluatorLabel} x auto`}
                    items={result.largeGapIndicators}
                    mode="gap"
                />
            </div>

            <ExpectedTable groups={scoreGroups} expectedContextLabel={expectedContextLabel} />

            <div className="space-y-5">
                <div className="flex items-center gap-2">
                    <ChartBarIcon className="h-5 w-5 text-primary-700" aria-hidden />
                    <h2 className="text-xl font-semibold text-gray-900">Detalhamento por critérios</h2>
                </div>
                {result.groups.map((group) => (
                    <GroupSection
                        editDraft={editDraft}
                        gapEvaluatorLabel={gapEvaluatorLabel}
                        key={group.id}
                        group={group}
                        isEditing={isEditing}
                        onAnswerChange={handleAnswerChange}
                        visibleTypes={visibleTypes}
                    />
                ))}
            </div>

            <ResultCommentsSection
                editDraft={editDraft}
                isEditing={isEditing}
                onCommentChange={handleCommentChange}
                result={result}
                visibleTypes={visibleTypes}
            />

            {isEditing ? (
                <QualitativeReportEditor
                    draft={qualitativeDraft}
                    error={qualitativeError}
                    loading={qualitativeLoading}
                    onNarrativeChange={handleQualitativeNarrativeChange}
                    onStringListChange={handleQualitativeStringListChange}
                />
            ) : null}
        </div>
    );
};
